import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { Provider } from 'react-redux';
import '../shared/styles/theme.css';
import '../../../../Content/css/theme/kanban.css';
import '../../../../Content/css/theme/kanban__visibility-buttons.css';
import '../../../../Content/css/theme/comments.css';
import '../../../../Content/css/theme/image-files.css';
import SelectProject from './components/SelectProject';
import { useTypedSelector } from './hooks/useTypedSelector';
import { useActions } from './hooks/useActions';
import localStorageService, { LocalStorageKeys } from '../../../services/localStorageService';
import sessionStorageService, { SessionStorageKeys } from '../../../services/sessionStorageService';
import SelectProjectRelease from './components/SelectProjectRelease';
import SearchBar from './components/SearchBar';
import DiagramContainer from './components/kanbanBoard/components/DiagramContainer';
import {
  Checkbox,
  ListItemText,
  ListItemIcon,
  Select,
  Button,
  MenuItem,
  Typography,
  Menu,
  CircularProgress,
  Tooltip,
  Box,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material';

import {
  PersonOff as PersonOffIcon,
  People as PeopleIcon,
  Groups as GroupsIcon,
  Person as PersonIcon,
  Archive as ArchiveIcon,
  InsertChart as InsertChartIcon,
  Sort as SortIcon,
  LowPriority as LowPriorityIcon,
  CalendarMonth as CalendarMonthIcon,
  SettingsApplications as SettingsApplicationsIcon,
  ArrowDropDown as ArrowDropDownIcon,
} from '@mui/icons-material';

import Estimations from './components/ReleaseEstimation';
import { QueryManagerHelper } from './helpers/QueryManagerHelper';
import { filterBySearchTerm, filterKanbanBySearchTerm } from './helpers/TasksHelper';
import type { IBoard, ICard } from './components/kanbanBoard/models/BoardModel';
import type { IProjectTaskModel } from './models/ProjectTaskModel';
import type { ProjectScope } from './models/TempProjectModel';
import KanbanBoard from './components/kanbanBoard';
import {
  EmptyComponent,
  ProjectsCard,
  ProjectsLaneHeader,
  ProjectsNewLaneSection,
} from './components/kanbanBoard/components';
import { projectsBoardStyle, projectsLaneStyle } from './styles/ProjectsKanbanStyles';
import AddNewEntityDialog from './components/AddNewEntityDialog';
import AddCardButton from './components/AddCardButton';
import { PhaseSortTypes } from './models/SortTypes';
import type { IEstimatable } from './models/IEstimatable';
import type { LocalizationEntity } from '../shared/models/localization';
import LocalizationContext from '../shared/contexts/LocalizationContext';
import LocalizationHelper from '../helpers/TranslationHelper';
import { ModalTypes } from './const/ModalTypes';
import axios from '../../../services/api';
import type { DeveloperWithHours } from './models/DeveloperWithHours';
import { NotAssignedProjectBox } from './components/NotAssignedProjectBox';
import type { ProjectStateError } from './types/project';
import { SuperPhaseTypeIds } from './const/SuperPhaseTypes';
import type { KanbanEventBusHandle } from './types/kanban';
import ConfirmTaskPhaseChangeDialog from './components/ConfirmTaskPhaseChangeDialog';
import { useChangeTaskPhaseConfirmDialog } from './hooks/customHooks';
import { useDebounce } from './hooks/useDebounce';
import type { IAssignedUserModel } from './models/AssignedUserModel';
import NoPermissionDialog from './components/NoPermissionDialog';
import { checkPermission, checkBoardAccess } from './helpers/PermissionsHelper';
import { fetchUserRoles } from '../helpers/userHelper';
import { ERROR_MESSAGES } from './models/constants';
import { PageKeyProvider } from '../shared/contexts/PageKeyContext';
import ProjectReleaseModal from './components/ProjectReleaseModal';
import { AccessDeniedBox } from './components/AccessDeniedBox';
import { KanbanViewContext } from './contexts/KanbanViewContext';
import type { KanbanView } from './contexts/KanbanViewContext';
import { TaskDetailModal } from './components/TaskDetailModal';
import { isActiveEmployeeRow } from '../../detail-form/employeeActivityUtils';
import { store as mainStore } from '../../../app/store';
import { gridApi } from '../../grid/api/gridApi';
import { loadRelationLookupData } from '../../detail-form/lookupDataLoader';
import { AssignTaskDialog } from './components/AssignTaskDialog';
import { AssignedUserPreviewPopover } from './components/AssignedUserPreviewPopover';

// Temporary: maps known integer node IDs to their Umbraco GUID keys
const NODE_KEY_MAP: Record<string, string> = {
  '4882': 'e81783f3-05a9-40e8-b704-09fcf651388f',
};

const Kanban: React.FC<{
  pageKey: string;
  /** Controlled view state — when provided Kanban uses this instead of internal state */
  view?: KanbanView;
  /** Fired whenever the view should change (task clicked or back pressed) */
  onViewChange?: (view: KanbanView) => void;
}> = ({ pageKey, view: controlledView, onViewChange }) => {
  const resolvedPageKey = NODE_KEY_MAP[pageKey] ?? pageKey;
  const localization = useContext<Array<LocalizationEntity>>(LocalizationContext);
  const routes = useTypedSelector((state: any) => state.kanban.routes);
  const { projects, project, errors, loading, board, currentSortingType, developers } =
    useTypedSelector((state: any) => state.project);
  const createTaskRouteId = routes?.createTaskRouteId ?? 0;

  const { isModalOpen, modalType, modalParameters, onCreateEntity, onCreateEntities } =
    useTypedSelector((state: any) => state.modal);

  const [hasPermissionError, setHasPermissionError] = useState<string | null>(null);
  const [revertLanes, setRevertLanes] = useState<any[] | null>(null);
  const taskPhaseRollbackRef = useRef<{ project: any; board: any } | null>(null);
  const targetBoardScrollRef = useRef<number | null>(null);
  const previousBoardContextRef = useRef<string | null>(null);
  const fetchProjectInFlightRef = useRef<number | null>(null);
  const [localView, setLocalView] = useState<KanbanView>({ type: 'board' });
  const [optimisticArchive, setOptimisticArchive] = useState<boolean | null>(null);
  const [assignmentTask, setAssignmentTask] = useState<{
    id: number;
    name: string;
    anchorEl: HTMLElement;
  } | null>(null);
  const [assignedUserPreview, setAssignedUserPreview] = useState<{
    user: IAssignedUserModel;
    anchorEl: HTMLElement;
  } | null>(null);

  useEffect(() => {
    if (!createTaskRouteId) return;

    // Let the board render first; the form and its lookups are warmed in the background.
    const preloadId = window.setTimeout(() => {
      const metadataRequest = mainStore.dispatch(
        gridApi.endpoints.getDetailMetadata.initiate(
          { detailFormId: createTaskRouteId },
          { subscribe: false }
        )
      );

      void metadataRequest
        .unwrap()
        .then((metadata) => {
          if (!metadata.fields?.length || !metadata.contentKey) return;

          return loadRelationLookupData({
            dispatch: mainStore.dispatch,
            fields: metadata.fields,
            contentKey: metadata.contentKey,
            mode: 'add',
            logPrefix: 'Kanban task form preload',
          });
        })
        .catch((error) => {
          console.warn('[Kanban task form preload] Failed to preload task form data.', error);
        });
    }, 0);

    return () => window.clearTimeout(preloadId);
  }, [createTaskRouteId]);

  // When DataSurfacePage passes `view` prop it takes full control of navigation.
  // When Kanban is used standalone (no parent controlling it), fall back to local state.
  const kanbanView = controlledView ?? localView;

  const isArchiveVisible =
    optimisticArchive !== null
      ? optimisticArchive
      : (project?.projectPhases?.some((phase: any) => phase.superPhaseName === 'ARCHIVE') ?? false);

  /**
   * Called when the user clicks a task card.
   *
   * How view state works here:
   *   - DataSurfacePage renders <Kanban view={kanbanView} onViewChange={setKanbanView} />.
   *   - When the user clicks a task, Kanban notifies the parent via onViewChange.
   *   - DataSurfacePage then unmounts Kanban and shows <KanbanTaskDetail> instead.
   *   - The breadcrumb in DataSurfacePage reflects the current sub-view.
   *
   * Why we pass editTaskRouteId:
   *   - KanbanTaskDetail needs to know WHICH detail-form to load for a task.
   *   - That form's Umbraco node ID is stored in the kanban routes config
   *     (fetched once via fetchKanbanRoutes on mount) as `routes.editTaskRouteId`.
   *   - We include it in the view descriptor so DataSurfacePage doesn't need to
   *     know anything about kanban routing internals.
   */
  const openTaskDetail = (taskId: number, taskName: string) => {
    const next: KanbanView = {
      type: 'task-detail',
      taskId,
      taskName,
      editTaskRouteId: routes?.editTaskRouteId ?? 0,
      developersNodeKey: routes?.getDevelopersNodeKey,
    };

    if (controlledView === undefined) {
      setLocalView(next);
    }

    onViewChange?.(next);
  };

  const backToBoard = () => {
    const next: KanbanView = { type: 'board' };
    if (controlledView === undefined) setLocalView(next);
    onViewChange?.(next);
  };

  const openTaskAssignment = (taskId: number, taskName: string, anchorEl: HTMLElement) => {
    setAssignmentTask({ id: taskId, name: taskName, anchorEl });
  };

  const openAssignedUserPreview = (user: IAssignedUserModel, anchorEl: HTMLElement) => {
    setAssignedUserPreview({ user, anchorEl });
  };

  useEffect(() => {
    if (!hasPermissionError && revertLanes && eventBusRef.current) {
      eventBusRef.current.publish({
        type: 'UPDATE_LANES',
        lanes: revertLanes,
      });
      setRevertLanes(null);
    }
  }, [hasPermissionError]);

  useEffect(() => {
    if (errors?.projectArchivePhases) {
      setOptimisticArchive(null);
    }
  }, [errors?.projectArchivePhases]);

  function getProjectError(errors: ProjectStateError | null | undefined): string | null {
    if (errors && errors.project) {
      return errors.project;
    }
    return null;
  }

  const {
    fetchCurrentProjects,
    fetchProject,
    fetchDevelopers,
    fetchKanbanRoutes,
    fetchProjectPhases,
    fetchProjectArchivePhase,
    setProjectArchivePhase,
    removeProjectArchivePhase,
    sortListOrder,
    changeTaskPhase,
    restoreTaskPhaseMove,
    closeProjectsModal,
    changeBoardSortingType,
  } = useActions();

  useEffect(() => {
    if (errors?.taskPhase && taskPhaseRollbackRef.current) {
      if (eventBusRef.current?.publish && taskPhaseRollbackRef.current.board?.lanes) {
        eventBusRef.current.publish({
          type: 'UPDATE_LANES',
          lanes: taskPhaseRollbackRef.current.board.lanes,
        });
      }

      restoreTaskPhaseMove(taskPhaseRollbackRef.current);
      taskPhaseRollbackRef.current = null;
    }
  }, [errors?.taskPhase, restoreTaskPhaseMove]);

  const getReleaseKey = (projectId: number) => `${SessionStorageKeys.CachedReleaseId}_${projectId}`;

  const getCachedProjectId = (): number | null => {
    const sessionId = sessionStorageService.get<number>(SessionStorageKeys.SessionProjectId);
    if (sessionId != null) return sessionId;

    const localIdRaw = localStorage.getItem(LocalStorageKeys.CachedLastProjectId);
    return localIdRaw ? Number(localIdRaw) : null;
  };

  const saveCachedProjectId = (projectId: number) => {
    sessionStorageService.save(SessionStorageKeys.SessionProjectId, projectId);
    localStorage.setItem(LocalStorageKeys.CachedLastProjectId, projectId.toString());
  };
  const getCachedReleaseId = (projectId: number): number | null => {
    const sessionReleaseId = sessionStorageService.get<number>(getReleaseKey(projectId));
    if (sessionReleaseId != null) return sessionReleaseId;

    const localReleaseRaw = localStorage.getItem(getReleaseKey(projectId));
    return localReleaseRaw ? Number(localReleaseRaw) : null;
  };

  const saveCachedReleaseId = (projectId: number, releaseId: number) => {
    sessionStorageService.save(getReleaseKey(projectId), releaseId);
    localStorage.setItem(getReleaseKey(projectId), releaseId.toString());
  };

  const getLocaleValue = (key: string, defaultValue: string) => {
    return LocalizationHelper.getLocaleEntityValue(localization, key, defaultValue);
  };

  const initialProjectIdParam =
    QueryManagerHelper.getParameterValue('projectId') ?? getCachedProjectId();
  const [selectedProjectId, setSelectedProjectId] = useState<number | null>(
    initialProjectIdParam ? Number(initialProjectIdParam) : null
  );
  const [selectedReleaseId, setSelectedReleaseId] = useState<number | null>(
    selectedProjectId ? getCachedReleaseId(selectedProjectId) : null
  );

  const [isSelectionModalOpen, setIsSelectionModalOpen] = useState(false);
  const [shouldFetchProject, setShouldFetchProject] = useState(false);
  const [isAdministrator, setIsAdministrator] = useState(false);
  const [projectScope, setProjectScope] = useState<ProjectScope>(
    () => localStorageService.get<ProjectScope>(LocalStorageKeys.CachedKanbanProjectScope) ?? 'all'
  );
  const [isProjectScopeRefreshing, setIsProjectScopeRefreshing] = useState(false);
  const [showEndedProjects, setShowEndedProjects] = useState(
    () => localStorageService.get<boolean>(LocalStorageKeys.CachedKanbanShowEndedProjects) ?? false
  );
  const suppressProjectSelectionModalRef = useRef(false);

  const refreshProjectList = (scope: ProjectScope, endedProjects: boolean) => {
    setIsProjectScopeRefreshing(true);
    Promise.resolve(fetchCurrentProjects(scope === 'all', true, endedProjects)).finally(() => {
      setIsProjectScopeRefreshing(false);
    });
  };

  const handleProjectScopeChange = (scope: ProjectScope) => {
    if (isProjectScopeRefreshing) return;
    suppressProjectSelectionModalRef.current = true;
    setProjectScope(scope);
    localStorageService.save(LocalStorageKeys.CachedKanbanProjectScope, scope);
    refreshProjectList(scope, showEndedProjects);
  };

  const handleShowEndedProjectsChange = (show: boolean) => {
    if (isProjectScopeRefreshing) return;
    suppressProjectSelectionModalRef.current = true;
    setShowEndedProjects(show);
    localStorageService.save(LocalStorageKeys.CachedKanbanShowEndedProjects, show);
    refreshProjectList(projectScope, show);
  };

  useEffect(() => {
    if (!loading && !errors?.projectArchivePhases) setOptimisticArchive(null);
  }, [loading, errors?.projectArchivePhases, selectedProjectId, project?.projectPhases]);

  useEffect(() => {
    if (!projects || projects.length === 0) return;

    const isProjectValid =
      selectedProjectId && projects.some((p: any) => p.id === selectedProjectId);

    if (!isProjectValid) {
      if (suppressProjectSelectionModalRef.current) {
        suppressProjectSelectionModalRef.current = false;
        setIsSelectionModalOpen(false);
        return;
      }

      setIsSelectionModalOpen(true);
      setShouldFetchProject(true);
      return;
    }

    suppressProjectSelectionModalRef.current = false;

    if (loading || !project || project.id !== selectedProjectId) return;

    const releases = project.projectReleases || [];
    const isReleaseSelectionRequired = releases.length > 0;

    if (isReleaseSelectionRequired && selectedReleaseId == null) {
      // Show the release-selection modal but do NOT set shouldFetchProject — doing so
      // would re-fetch the project on every render and create an infinite request loop.
      setIsSelectionModalOpen(true);
    } else {
      setIsSelectionModalOpen(false);
    }
  }, [selectedProjectId, selectedReleaseId, projects, project, loading]);

  const handleModalSelection = (projectId: number, releaseId: number | null) => {
    setSelectedProjectId(projectId);
    setSelectedReleaseId(releaseId);
  };
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [selectedSortType, setSelectedSortType] = useState(currentSortingType);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [sortIcon, setSortIcon] = useState<ReactElement>(<SortIcon />);
  const [selectedUsers, setSelectedUsers] = useState<number[]>([]);
  const [showUnassigned, setShowUnassigned] = useState(false);
  const [hideInactive, setHideInactive] = useState(false);
  const [isUserAssigned, setIsUserAssinged] = useState(true);
  const { showConfirmDialog, showConfirmTaskPhaseChangeDialog } = useChangeTaskPhaseConfirmDialog();

  const [usersMenu, setUsersMenu] = useState<boolean>(false);
  const [load, setLoading] = useState(false);
  const [usersList, setUsers] = useState<DeveloperWithHours[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [openCumulativeDiagram, setOpenCumulativeDiagram] = useState(false);

  const filteredBoard = useMemo(() => {
    if (!board) {
      return undefined;
    }

    return {
      ...board,
      lanes: board.lanes.map((lane: any) => {
        const filteredCards = filterKanbanBySearchTerm(lane.cards, debouncedSearchTerm).filter(
          (card: any) => {
            const assignedUser = card.content?.assignedUser;
            const isUnassigned = !assignedUser?.id;

            if (!isUnassigned && hideInactive) {
              const userInList = usersList.find((u) => u.crmRelationId === assignedUser?.id);
              const isUserActive = userInList
                ? userInList.isActive || (userInList.totalHours && userInList.totalHours > 0)
                : false;
              if (!isUserActive) return false;
            }

            if (showUnassigned && selectedUsers.length === 0) {
              return isUnassigned;
            }
            if (!showUnassigned && selectedUsers.length > 0) {
              return selectedUsers.includes(assignedUser?.id);
            }
            if (showUnassigned && selectedUsers.length > 0) {
              return isUnassigned || selectedUsers.includes(assignedUser?.id);
            }
            return true;
          }
        );

        return { ...lane, cards: filteredCards.map((card) => ({ ...card })) };
      }),
    };
  }, [debouncedSearchTerm, board, selectedUsers, showUnassigned, hideInactive, usersList]);

  const eventBusRef = useRef<KanbanEventBusHandle | null>(null);
  const setEventBus = (handle: KanbanEventBusHandle | null) => {
    eventBusRef.current = handle;
  };

  const isEstimateMissing = (task?: IProjectTaskModel | null) => !task?.taskEstimateTime;
  const isReleaseEstimateMissing = (task?: IProjectTaskModel | null) =>
    !task?.taskEstimateTimeInRelease;
  const hasRelease = (task?: IProjectTaskModel | null) => !!task?.projectReleasesId;

  const handleConfirmTaskPhaseChangeDialog = async (
    newLaneId: string,
    oldLandId: string,
    oldPosition: number,
    task: IProjectTaskModel,
    cardId: string,
    isReleaseEstimation: boolean
  ): Promise<{ success: boolean; updatedTask: IProjectTaskModel }> => {
    const result = await showConfirmDialog(cardId, isReleaseEstimation);
    if (!result.success) {
      if (eventBusRef.current) {
        eventBusRef.current.publish({
          type: 'MOVE_CARD',
          fromLaneId: newLaneId,
          toLaneId: oldLandId,
          cardId: cardId,
          index: oldPosition,
        });
      }
      return { success: result.success, updatedTask: task };
    }

    const updatedTask = { ...task };
    if (isReleaseEstimation) updatedTask.taskEstimateTimeInRelease = result.estimation;
    else updatedTask.taskEstimateTime = result.estimation;

    return { success: result.success, updatedTask };
  };

  const handlePhaseMove = async (): Promise<boolean> => {
    const hasPermission = await checkPermission('movePhase', pageKey);
    const lanesSnapshot = [...(filteredBoard?.lanes ?? [])];

    if (!hasPermission) {
      setHasPermissionError(ERROR_MESSAGES.MOVE_NO_PERMISSION);
      setRevertLanes(lanesSnapshot);
    }

    return hasPermission;
  };

  const [hasBoardAccess, setHasBoardAccess] = useState<boolean | null>(null);
  useEffect(() => {
    const checkAccess = async () => {
      const hasAccess = await checkBoardAccess(resolvedPageKey);
      setHasBoardAccess(hasAccess);
    };
    checkAccess();
  }, [pageKey]);
  const handleSelectChange = (event: SelectChangeEvent<number[]>) => {
    const value = event.target.value;
    if (Array.isArray(value)) {
      if (value.includes(-1)) {
        setSelectedUsers([]);
        setShowUnassigned(false);
        setHideInactive(false);
        return;
      } else if (value.includes(-2)) {
        setShowUnassigned((prev) => !prev);
      } else if (value.includes(-3)) {
        setHideInactive((prev) => !prev);
      } else {
        const numericValues = value.filter((v: any) => !isNaN(v)).map(Number);
        setSelectedUsers(numericValues);
      }
    }
  };

  const sortMenuItems = [
    {
      name: 'custom',
      hint: getLocaleValue('ZSite.Frontend.Kanban.Hint.SortButton.Custom', 'Custom sorting'),
      value: PhaseSortTypes.Default,
      icon: <SortIcon />,
    },
    {
      name: 'priority',
      hint: getLocaleValue('ZSite.Frontend.Kanban.Hint.SortButton.Priority', 'Sorting by priority'),
      value: PhaseSortTypes.Priority,
      icon: <LowPriorityIcon />,
    },
    {
      name: 'date',
      hint: getLocaleValue('ZSite.Frontend.Kanban.Hint.SortButton.Date', 'Sorting by date'),
      value: PhaseSortTypes.Newest,
      icon: <CalendarMonthIcon />,
    },
  ];

  const changeSortType = (sortingType: number): void => {
    localStorageService.save(LocalStorageKeys.CachedSortType, sortingType);
    setSelectedSortType(sortingType);
    changeBoardSortingType(sortingType);
    const item = sortMenuItems.find((x) => x.value === sortingType);
    if (item) setSortIcon(item.icon);
    setSortAnchorEl(null);
  };

  const closeModal = (): void => {
    closeProjectsModal();
  };

  const handleOnColumnDragEnd = async (
    removedIndex: string,
    addedIndex: string,
    draggedLane?: { id?: string | number }
  ) => {
    const previousLanes = [...(filteredBoard?.lanes ?? [])];
    const hasPermission = await handlePhaseMove();

    if (!hasPermission) {
      return;
    }

    const phases = project?.projectPhases ?? [];
    const visibleLanes = filteredBoard?.lanes ?? [];
    const draggedLaneId = draggedLane?.id?.toString();
    const sourceDisplayIndex = Number(removedIndex);
    const targetDisplayIndex = Number(addedIndex);
    const draggedPhaseIndex = phases.findIndex(
      (phase: any) => phase.id.toString() === draggedLaneId
    );

    let indexStart = sourceDisplayIndex;
    let indexEnd = targetDisplayIndex;

    if (draggedLaneId && draggedPhaseIndex !== -1) {
      const reorderedVisibleIds = visibleLanes.map((lane: any) => lane.id.toString());
      const [movedLaneId] = reorderedVisibleIds.splice(sourceDisplayIndex, 1);

      if (movedLaneId) {
        reorderedVisibleIds.splice(targetDisplayIndex, 0, movedLaneId);

        // Keep hidden phases in their original slots while applying the new
        // order to the lanes the user actually dragged.
        const visiblePhaseIds = new Set(reorderedVisibleIds);
        const desiredPhaseIds = phases.map((phase: any) => phase.id.toString());
        let visibleIndex = 0;
        desiredPhaseIds.forEach((phaseId: string, index: number) => {
          if (visiblePhaseIds.has(phaseId)) {
            desiredPhaseIds[index] = reorderedVisibleIds[visibleIndex++];
          }
        });

        indexStart = draggedPhaseIndex;
        indexEnd = desiredPhaseIds.indexOf(draggedLaneId);
      }
    }

    if (indexStart === indexEnd) return;

    const persisted = await sortListOrder(project.id, indexStart, indexEnd);
    if (!persisted && eventBusRef.current) {
      eventBusRef.current.publish({
        type: 'UPDATE_LANES',
        lanes: previousLanes,
      });
    }
  };

  const handleOnCardDragEnd = async (
    cardId: string,
    sourceLandId: string,
    targetLaneId: string,
    position: number
  ) => {
    try {
      if (!project?.projectPhases) return;

      const destination = project.projectPhases.find(
        (phase: any) => phase.id === Number(targetLaneId)
      );
      const source = project.projectPhases.find((phase: any) => phase.id === Number(sourceLandId));

      if (!source) {
        console.error('[Kanban] handleOnCardDragEnd: source phase not found', {
          sourceLandId,
          phases: project.projectPhases.map((p: any) => p.id),
        });
        return;
      }
      if (!destination) {
        console.error('[Kanban] handleOnCardDragEnd: destination phase not found', {
          targetLaneId,
          phases: project.projectPhases.map((p: any) => p.id),
        });
        return;
      }

      const indexStart = source.phaseTasks.findIndex(
        (card: any) => String(card.id) === String(cardId)
      );
      const filteredDestinationPhaseTasks = filterBySearchTerm(destination.phaseTasks, searchTerm);
      const currentTask = source.phaseTasks.find((card: any) => String(card.id) === String(cardId));
      if (!currentTask) return;

      const oldTask = filteredDestinationPhaseTasks[position];
      const indexEnd = !oldTask ? position : destination.phaseTasks.indexOf(oldTask);
      const isMovingToInProgressSuperPhase =
        destination?.superPhaseId === SuperPhaseTypeIds.IN_PROGRESS;

      // Keep the current position while react-trello rebuilds its lanes after the drop.
      targetBoardScrollRef.current = kanbanBoardRef.current?.scrollLeft ?? null;

      // Determine which estimation is missing
      const isMissingEstimate = isEstimateMissing(currentTask);
      const isMissingReleaseEstimate = isReleaseEstimateMissing(currentTask);
      const hasProjectRelease = hasRelease(currentTask);

      if (
        isMovingToInProgressSuperPhase &&
        ((isMissingEstimate && !hasProjectRelease) ||
          (isMissingReleaseEstimate && hasProjectRelease))
      ) {
        const { success, updatedTask } = await handleConfirmTaskPhaseChangeDialog(
          targetLaneId,
          sourceLandId,
          indexStart,
          currentTask,
          cardId,
          hasProjectRelease
        );
        if (!success) {
          targetBoardScrollRef.current = null;
          return;
        }

        // Optimistically reflect the entered estimate on the card without mutating Redux state directly
        if (eventBusRef.current) {
          const updatedLanes = filteredBoard?.lanes?.map((lane: any) => ({
            ...lane,
            cards: lane.cards.map((card: any) =>
              String(card.id) === String(cardId) ? { ...card, content: updatedTask } : card
            ),
          }));
          if (updatedLanes) {
            eventBusRef.current.publish({
              type: 'UPDATE_LANES',
              lanes: updatedLanes,
            });
          }
        }
      }

      if (boardScrollTimeoutRef.current !== null) {
        window.clearTimeout(boardScrollTimeoutRef.current);
        boardScrollTimeoutRef.current = null;
      }

      taskPhaseRollbackRef.current = {
        project,
        board,
      };

      const wasChanged = Boolean(
        await changeTaskPhase(
          sourceLandId,
          targetLaneId,
          indexStart,
          indexEnd,
          cardId,
          selectedReleaseId,
          selectedSortType
        )
      );

      if (wasChanged) {
        taskPhaseRollbackRef.current = null;
      } else {
        targetBoardScrollRef.current = null;
      }
    } catch (e) {
      targetBoardScrollRef.current = null;
      console.error('[Kanban] handleOnCardDragEnd error:', e);
    }
  };

  useEffect(() => {
    fetchKanbanRoutes(resolvedPageKey);
    fetchUserRoles().then((roles) => {
      const administrator =
        Array.isArray(roles) &&
        roles.some((role: string) =>
          ['administrators', 'supervisor', 'company administrator'].includes(
            role.trim().toLowerCase()
          )
        );
      setIsAdministrator(administrator);
      fetchCurrentProjects(
        administrator && projectScope === 'all',
        false,
        administrator && showEndedProjects
      );
    });
  }, []);

  useEffect(() => {
    if (!loading) setOptimisticArchive(null);
  }, [loading, selectedProjectId, project?.projectPhases]);

  useEffect(() => {
    if (projects?.length > 0) {
      const cachedProjectId = getCachedProjectId();
      const isCachedProjectValid =
        cachedProjectId && projects.some((project: any) => project.id === cachedProjectId);
      if (isCachedProjectValid && cachedProjectId !== selectedProjectId) {
        setSelectedProjectId(cachedProjectId);
      } else if (!isCachedProjectValid && selectedProjectId == null) {
        if (suppressProjectSelectionModalRef.current) return;
        setSelectedProjectId(null);
        setIsSelectionModalOpen(true);
        setShouldFetchProject(true);
      }
    }
  }, [projects]);

  useEffect(() => {
    if (projects && projects?.length > 0) {
      checkUserAssignment();
    }
  }, [projects, selectedProjectId, isAdministrator]);

  useEffect(() => {
    if (board && board.boardId !== selectedProjectId) {
      setSelectedReleaseId(null);
    }
  }, [board, selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId || !project || project.id !== selectedProjectId) {
      return;
    }

    const cachedReleaseId = getCachedReleaseId(selectedProjectId);
    const releases = project.projectReleases || [];

    if (
      cachedReleaseId != null &&
      (releases.some((r: any) => r.releaseId === cachedReleaseId) || cachedReleaseId === 0)
    ) {
      setSelectedReleaseId((current) => (current === cachedReleaseId ? current : cachedReleaseId));
    } else if (releases.length === 0) {
      setSelectedReleaseId((current) => (current === 0 ? current : 0));
    } else {
      setSelectedReleaseId((current) => (current === null ? current : null));
    }
  }, [project]);

  // 1. Cache selected project id + reflect it in the URL query param.
  //    Only depends on selectedProjectId.
  useEffect(() => {
    setSearchTerm('');
    if (selectedProjectId === null) return;

    const cachedProject = getCachedProjectId();
    if (cachedProject !== selectedProjectId) {
      saveCachedProjectId(selectedProjectId);
    }
    QueryManagerHelper.appendQueryParameter('projectId', selectedProjectId.toString());
  }, [selectedProjectId]);

  // 2. Fetch the project itself. Fires once when selectedProjectId changes to something
  //    Redux doesn't have yet. Re-runs when project.id catches up and exits immediately
  //    via the guard — it does NOT re-fetch.

  useEffect(() => {
    if (selectedProjectId == null) return;
    if (fetchProjectInFlightRef.current === selectedProjectId) return;
    fetchProjectInFlightRef.current = selectedProjectId;
    fetchProject(selectedProjectId);
  }, [selectedProjectId]);

  // 3. Explicit forced refetch (e.g. after creating a task via TaskDetailModal.onCreated).
  //    Intentionally isolated from selectedProjectId/routes so it doesn't drag
  //    fetchProjectPhases along with it.
  useEffect(() => {
    if (!shouldFetchProject || selectedProjectId == null) return;
    fetchProject(selectedProjectId);
    setShouldFetchProject((current) => (current ? false : current));
  }, [shouldFetchProject]);

  // 4. Fetch developers. Genuinely depends on both selectedProjectId AND routes
  //    (needs routes.getDevelopersNodeKey), so both stay in deps here — but this
  //    no longer drags fetchProjectPhases along with it when routes arrives.
  useEffect(() => {
    if (selectedProjectId == null || !routes) return;
    fetchDevelopers(routes.getDevelopersNodeKey, selectedProjectId);
  }, [selectedProjectId, routes]);

  // 5. Fetch project phases. Only depends on selectedProjectId (+ cached release).
  //    routes/shouldFetchProject are deliberately NOT in this dependency array —
  //    they were the cause of the duplicate 776 kB GetProjectPhasesByProjectId calls.
  useEffect(() => {
    if (selectedProjectId == null) return;

    const currentRelease = getCachedReleaseId(selectedProjectId);
    if (currentRelease != null) {
      fetchProjectPhases(selectedProjectId, currentRelease > 0 ? currentRelease : undefined);
      setSelectedReleaseId((current) => (current === currentRelease ? current : currentRelease));
    } else if (project?.id === selectedProjectId && project.projectReleases?.length === 0) {
      setSelectedReleaseId((current) => (current === 0 ? current : 0));
    } else {
      setSelectedReleaseId((current) => (current === null ? current : null));
    }
  }, [selectedProjectId]);

  useEffect(() => {
    if (selectedProjectId === null && selectedReleaseId == null) return;

    const cachedRelease = getCachedReleaseId(selectedProjectId!);
    if (
      cachedRelease !== selectedReleaseId &&
      selectedProjectId !== null &&
      selectedReleaseId != null &&
      project?.id === selectedProjectId
    ) {
      saveCachedReleaseId(selectedProjectId, selectedReleaseId);
      fetchProjectPhases(selectedProjectId, selectedReleaseId! > 0 ? selectedReleaseId : undefined);
    }
  }, [selectedProjectId, selectedReleaseId]);

  const renderContent =
    !loading && !!project && !!filteredBoard?.lanes && filteredBoard?.boardId == selectedProjectId;

  const allCards: ICard<IEstimatable>[] = filteredBoard?.lanes?.flatMap((x: any) => x.cards) ?? [];

  const handleMenuClick = (event: React.SyntheticEvent<Element, Event>) => {
    fetchUsersData();
    setAnchorEl(event.currentTarget as HTMLElement);
    setUsersMenu(true);
  };

  const handleArchiveTaskClickOptimistic = () => {
    if (!project || selectedProjectId == null || loading) return;

    const willToggle =
      project.projectArchivePhases != null ||
      project.projectPhases?.some((phase: any) => phase.superPhaseName === 'ARCHIVE');

    if (willToggle) {
      setOptimisticArchive(!isArchiveVisible);
    }
    handleArchiveTaskClick();
  };

  const handleArchiveTaskClick = () => {
    if (!project || selectedProjectId == null) return;
    if (!project.projectArchivePhases) {
      fetchProjectArchivePhase(selectedProjectId);
    } else if (project.projectPhases.some((phase: any) => phase.superPhaseName === 'ARCHIVE')) {
      removeProjectArchivePhase(selectedProjectId);
    } else if (project.projectArchivePhases.length > 0) {
      setProjectArchivePhase(selectedProjectId);
    }
  };
  const fetchUsersData = async () => {
    if (!project || !project.projectPhases) return;
    setLoading(true);
    try {
      const response = await axios.post(
        '/umbraco/api/Grid/GetGridDataWithParametersAsync?nodeKey=' +
          routes.getAssignedToProjectNodeKey,
        { ProjectId: selectedProjectId }
      );

      const result: DeveloperWithHours[] = response.data || [];
      const uniqueAssignedUsers = Array.from(
        new Map(
          project.projectPhases
            .flatMap((phase: any) => phase.phaseTasks)
            .map((task: any) => task.assignedUser)
            .filter((user: any): user is IAssignedUserModel => user !== null)
            .map((user: any) => [user.id, user])
        ).values()
      );

      const uniqueNames = new Set(result.map((user: any) => user.crmRelationName));

      uniqueAssignedUsers.forEach((user: any) => {
        if (!uniqueNames.has(user.fullName)) {
          const userWithHours: DeveloperWithHours = {
            crmRelationId: user.id,
            crmRelationName: user.fullName,
            totalHours: null,
            hour: null,
            isActive: false,
          };

          result.push(userWithHours);
          uniqueNames.add(user.fullName);
        }
      });

      setUsers(result);
    } catch (error) {
      console.error('Error fetching data', error);
    }
    setLoading(false);
  };

  const checkUserAssignment = async () => {
    const roles = await fetchUserRoles();
    const supervisorRoles: string[] = ['administrators', 'supervisor'];

    if (
      projects.filter((project: any) => project.id === selectedProjectId).length > 0 ||
      isAdministrator ||
      (Array.isArray(roles) &&
        roles.some((role: string) => supervisorRoles.includes(role.toLowerCase())))
    ) {
      setIsUserAssinged(true);
    } else {
      setIsUserAssinged(false);
    }
  };

  const kanbanBoardRef = useRef<HTMLDivElement>(null);

  function getHorizontalBoardContainer(containerRef: any) {
    if (!containerRef) return null;
    if (containerRef.matches?.('.smooth-dnd-container.horizontal')) return containerRef;
    return containerRef.querySelector?.('.smooth-dnd-container.horizontal') ?? null;
  }

  function extractElementsFromSections(containerRef: any) {
    const container = getHorizontalBoardContainer(containerRef);
    if (!container) return [];

    const draggableWrappers = container.querySelectorAll('.smooth-dnd-draggable-wrapper > section');

    const elements: any[] = [];

    draggableWrappers.forEach((section: any) => {
      const firstTwoChildren = section.children[1];
      elements.push(firstTwoChildren);
    });

    return elements;
  }

  const getLineScrollStorageKey = (element: HTMLElement): string | null => {
    const lane = element.closest('section');
    const laneId = lane?.getAttribute('label');

    if (!laneId) return null;

    return `${LocalStorageKeys.CachedLineScrollBar}_${resolvedPageKey}_${selectedProjectId}_${laneId}`;
  };

  const getBoardScrollStorageKey = () =>
    `${LocalStorageKeys.CachedScrollBar}_${resolvedPageKey}_${selectedProjectId}`;

  useEffect(() => {
    if (project && project.projectReleases?.length == 0) {
      setSelectedReleaseId((current) => (current === 0 ? current : 0));
    }
  }, [filteredBoard, project]);

  const scrollToElement = (element: any, position: any) => {
    if (parseInt(position) > 0) {
      element.scrollTo(0, parseInt(position));
    } else {
      localStorageService.remove(element.getAttribute('data-element-id'));
    }
  };

  const loadData = (restoreScroll: boolean) => {
    if (kanbanBoardRef.current) {
      if (restoreScroll || targetBoardScrollRef.current !== null) {
        setCurrentScroll();
      }

      const elements = extractElementsFromSections(kanbanBoardRef.current);

      if (!elements || !Array.isArray(elements)) return;

      elements.forEach((element) => {
        const elementId = element instanceof HTMLElement ? getLineScrollStorageKey(element) : null;
        if (!elementId) return;

        if (element instanceof HTMLElement) {
          element.setAttribute('data-element-id', elementId);
          element.addEventListener('scroll', lineHandleScroll);
        }

        const savedPosition = restoreScroll ? localStorageService.get(elementId)?.toString() : null;

        if (savedPosition) scrollToElement(element, savedPosition);
      });
    }
  };

  const setCurrentScroll = () => {
    const targetScrollX = targetBoardScrollRef.current;
    if (targetScrollX !== null) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          const container = kanbanBoardRef.current;
          if (!container) return;

          container.scrollTo({ left: targetScrollX, top: 0, behavior: 'auto' });
          targetBoardScrollRef.current = null;
        });
      });
      return;
    }

    const savedScrollX = localStorageService.get(getBoardScrollStorageKey())?.toString().split('/');

    if (savedScrollX && parseInt(savedScrollX[1]) === selectedProjectId) {
      requestAnimationFrame(() => {
        if (kanbanBoardRef.current)
          kanbanBoardRef.current.scrollTo({
            left: Number(savedScrollX[0]),
            top: 0,
            behavior: 'auto',
          });
      });
    }
  };

  const lineScrollTimeoutsRef = useRef<Record<string, number>>({});
  const boardScrollTimeoutRef = useRef<number | null>(null);

  const saveLineScrollDebounced = (elementId: string, scrollTop: number) => {
    const timeoutId = lineScrollTimeoutsRef.current[elementId];
    if (timeoutId) {
      window.clearTimeout(timeoutId);
    }

    lineScrollTimeoutsRef.current[elementId] = window.setTimeout(() => {
      localStorageService.save(elementId, scrollTop.toString());
      delete lineScrollTimeoutsRef.current[elementId];
    }, 400);
  };

  const saveBoardScrollDebounced = (scrollLeft: number) => {
    if (boardScrollTimeoutRef.current !== null) {
      window.clearTimeout(boardScrollTimeoutRef.current);
    }

    boardScrollTimeoutRef.current = window.setTimeout(() => {
      localStorageService.save(
        getBoardScrollStorageKey(),
        scrollLeft.toString() + '/' + (selectedProjectId ?? '').toString()
      );
      boardScrollTimeoutRef.current = null;
    }, 400);
  };

  const lineHandleScroll = (e: any) => {
    const elementId = e.target.getAttribute('data-element-id');
    if (!elementId) {
      return;
    }

    saveLineScrollDebounced(elementId, e.target.scrollTop);
  };

  const handleScroll = () => {
    const container = kanbanBoardRef?.current;
    if (container && targetBoardScrollRef.current === null) {
      saveBoardScrollDebounced(container.scrollLeft);
    }
  };

  useEffect(() => {
    return () => {
      Object.values(lineScrollTimeoutsRef.current).forEach((timeoutId) => {
        window.clearTimeout(timeoutId);
      });

      if (boardScrollTimeoutRef.current !== null) {
        window.clearTimeout(boardScrollTimeoutRef.current);
      }
    };
  }, []);

  const iconMap: { [key: number]: ReactElement } = {
    0: <PersonOffIcon />,
    1: <PersonIcon />,
    2: <PeopleIcon />,
  };

  const renderSelectedIcon = () => {
    const selectedCount = selectedUsers.length;
    return iconMap[selectedCount] || <GroupsIcon />;
  };
  const renderArchiveIcon = () => {
    return <ArchiveIcon />;
  };

  const renderChartIcon = () => {
    return <InsertChartIcon />;
  };
  const anyError = getProjectError(errors);
  const canOpenTaskDetail = Boolean((routes?.editTaskRouteId ?? 0) > 0);
  const customCardComponent = useMemo(
    () =>
      ((props: any) => (
        <ProjectsCard
          {...props}
          editTaskRoute={routes?.editTaskRoute}
          canOpenTaskDetail={canOpenTaskDetail}
        />
      )) as any,
    [routes?.editTaskRoute, canOpenTaskDetail]
  );

  useLayoutEffect(() => {
    const container = kanbanBoardRef?.current;

    if (container) {
      container.addEventListener('scroll', handleScroll);
    }

    const animationFrame = requestAnimationFrame(() => {
      if (!renderContent || !kanbanBoardRef.current) return;

      const boardContext = `${resolvedPageKey}:${selectedProjectId ?? ''}`;
      const restoreScroll =
        previousBoardContextRef.current !== null &&
        previousBoardContextRef.current !== boardContext;
      previousBoardContextRef.current = boardContext;
      loadData(restoreScroll);
    });

    return () => {
      cancelAnimationFrame(animationFrame);

      if (container) {
        container.removeEventListener('scroll', handleScroll);
      }

      if (container) {
        const elements = extractElementsFromSections(container);

        elements.forEach((element) => {
          if (element && typeof element.removeEventListener === 'function') {
            element.removeEventListener('scroll', lineHandleScroll);
          }
        });
      }
    };
  }, [renderContent, filteredBoard, selectedProjectId, resolvedPageKey]);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const currentProjectName = project && isUserAssigned ? project?.projectName : '';

  if (hasBoardAccess === null) {
    return (
      <Box sx={{ pl: 2.5, pt: 2.5 }}>
        <h5>
          {LocalizationHelper.getLocaleEntityValue(
            localization,
            'ZSite.Frontend.Kanban.Loading',
            'Loading...'
          )}
        </h5>
      </Box>
    );
  }
  if (!hasBoardAccess) {
    return <AccessDeniedBox />;
  }
  return (
    <KanbanViewContext.Provider
      value={{
        view: kanbanView,
        openTaskDetail,
        openTaskAssignment,
        openAssignedUserPreview,
        refreshProject: () => {
          if (selectedProjectId != null) {
            void fetchProjectPhases(
              selectedProjectId,
              selectedReleaseId != null && selectedReleaseId > 0 ? selectedReleaseId : undefined
            );
          }
        },
        backToBoard,
      }}
    >
      <div className="kanban-board">
        {isUserAssigned && !anyError ? (
          <div>
            <div className="kanban-board__header d-flex">
              <div className="estimations">
                <div className="top-container">
                  <AddCardButton board={filteredBoard as any} disabled={!renderContent} />
                  <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
                </div>
                {project && <Estimations cards={allCards} projectReleaseId={selectedReleaseId} />}
              </div>
              <div className="selectors">
                <SelectProject
                  onChange={(projectId: any) => {
                    setSelectedReleaseId(null);
                    setSelectedProjectId(projectId);
                  }}
                  projects={projects}
                  selectProjectId={selectedProjectId!}
                  currentProjectName={currentProjectName}
                  renderContent={!renderContent}
                  isAdministrator={isAdministrator}
                  projectScope={projectScope}
                  onProjectScopeChange={handleProjectScopeChange}
                  isRefreshing={isProjectScopeRefreshing}
                  showEndedProjects={showEndedProjects}
                  onShowEndedProjectsChange={handleShowEndedProjectsChange}
                  disablePortal
                />
                {project && project.projectReleases?.length > 0 && (
                  <SelectProjectRelease
                    onChange={setSelectedReleaseId}
                    releases={project.projectReleases}
                    defaultReleaseId={(selectedReleaseId ?? 0) as number}
                    routes={routes?.editProjectReleaseDetailRoute}
                    renderContent={!renderContent}
                    disablePortal={false}
                  />
                )}
              </div>
              <div
                className="view-mode-buttons"
                style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}
              >
                {/* Plain action buttons */}
                <Tooltip
                  title={isArchiveVisible ? 'Hide archived tasks' : 'Show archived tasks'}
                  classes={{ popper: 'custom-tooltip' }}
                >
                  <Button
                    variant={isArchiveVisible ? 'contained' : 'outlined'}
                    color={isArchiveVisible ? 'warning' : 'primary'}
                    disabled={!project || loading || selectedProjectId == null}
                    onClick={handleArchiveTaskClickOptimistic}
                  >
                    {renderArchiveIcon()}
                  </Button>
                </Tooltip>
                <Tooltip title="Сumulative flow diagram" classes={{ popper: 'custom-tooltip' }}>
                  <Button
                    variant="outlined"
                    onClick={() => setOpenCumulativeDiagram((prev) => !prev)}
                  >
                    {renderChartIcon()}
                  </Button>
                </Tooltip>
                <Tooltip title="Project Settings" classes={{ popper: 'custom-tooltip' }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      if (routes)
                        window.location.href = `${routes.projectDetailsGridUrl}/${selectedProjectId}?gridContentKey=${routes.projectDetailsGridContentKey}`;
                    }}
                  >
                    <SettingsApplicationsIcon />
                  </Button>
                </Tooltip>

                {/* Dropdown buttons grouped on the right */}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <Tooltip title="Assigned developers" classes={{ popper: 'custom-tooltip' }}>
                    <Button
                      variant="outlined"
                      onClick={handleMenuClick}
                      endIcon={<ArrowDropDownIcon />}
                    >
                      {renderSelectedIcon()}
                    </Button>
                  </Tooltip>
                  {usersMenu &&
                    (usersList.length > 0 ? (
                      <Select
                        multiple
                        value={selectedUsers}
                        onChange={handleSelectChange}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        MenuProps={{
                          anchorEl: anchorEl,
                          keepMounted: true,
                          anchorOrigin: {
                            vertical: 'bottom',
                            horizontal: 'left',
                          },
                        }}
                        renderValue={() => null}
                        style={{ display: 'none' }}
                      >
                        <MenuItem
                          disabled={selectedUsers.length === 0 && !showUnassigned && !hideInactive}
                          key={-1}
                          value={-1}
                        >
                          <Button
                            fullWidth
                            variant="contained"
                            color="primary"
                            sx={{
                              textTransform: 'none',
                              fontSize: '1rem',
                              fontWeight: 400,
                              lineHeight: 1.5,
                            }}
                          >
                            {LocalizationHelper.getLocaleEntityValue(
                              localization,
                              'ZSite.Frontend.Kanban.Hint.UnselectAll',
                              'Unselect All'
                            )}
                          </Button>
                        </MenuItem>
                        <MenuItem key={-2} value={-2}>
                          <Checkbox checked={showUnassigned} />
                          <ListItemText
                            primary={LocalizationHelper.getLocaleEntityValue(
                              localization,
                              'ZSite.Frontend.Kanban.Hint.Unassigned',
                              'Unassigned'
                            )}
                          />
                        </MenuItem>
                        <MenuItem key={-3} value={-3}>
                          <Checkbox checked={hideInactive} />
                          <ListItemText
                            primary={LocalizationHelper.getLocaleEntityValue(
                              localization,
                              'ZSite.Frontend.Kanban.Hint.HideInactive',
                              'Hide Inactive'
                            )}
                          />
                        </MenuItem>
                        {usersList
                          .filter((developer) => {
                            if (hideInactive) {
                              return (
                                developer.isActive ||
                                (developer.totalHours && developer.totalHours > 0)
                              );
                            }
                            return true;
                          })
                          .map((developer) => (
                            <MenuItem key={developer.crmRelationId} value={developer.crmRelationId}>
                              <Checkbox checked={selectedUsers.includes(developer.crmRelationId)} />
                              <ListItemText
                                primary={`${developer.crmRelationName} (${developer.isActive || (developer.totalHours && developer.totalHours > 0) ? `${developer.totalHours} of ${developer.hour} hrs` : 'inactive'})`}
                              />
                            </MenuItem>
                          ))}
                      </Select>
                    ) : (
                      <Menu
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                        keepMounted
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'left',
                        }}
                      >
                        <MenuItem disabled>
                          {load ? (
                            <CircularProgress size={50} />
                          ) : (
                            <Typography variant="body1" color="textPrimary">
                              No developers assigned to this project
                            </Typography>
                          )}
                        </MenuItem>
                      </Menu>
                    ))}

                  <Tooltip title="Sort tasks" classes={{ popper: 'custom-tooltip' }}>
                    <Button
                      variant="outlined"
                      onClick={(e) => setSortAnchorEl(e.currentTarget)}
                      aria-haspopup="true"
                      aria-expanded={sortAnchorEl ? 'true' : undefined}
                      endIcon={<ArrowDropDownIcon />}
                    >
                      {sortIcon}
                    </Button>
                  </Tooltip>
                  <Menu
                    anchorEl={sortAnchorEl}
                    open={Boolean(sortAnchorEl)}
                    onClose={() => setSortAnchorEl(null)}
                  >
                    {sortMenuItems.map((item) => (
                      <MenuItem
                        key={item.value}
                        selected={item.value === selectedSortType}
                        onClick={() => changeSortType(item.value)}
                      >
                        <ListItemIcon>{item.icon}</ListItemIcon>
                        <ListItemText>{item.hint}</ListItemText>
                      </MenuItem>
                    ))}
                  </Menu>
                </div>
              </div>
            </div>
            {renderContent ? (
              <div ref={kanbanBoardRef} style={{ overflowX: 'auto' }}>
                <PageKeyProvider pageKey={resolvedPageKey}>
                  <KanbanBoard
                    initialBoard={
                      filteredBoard ??
                      ({
                        boardId: selectedProjectId ?? undefined,
                        lanes: [],
                      } as IBoard<IProjectTaskModel>)
                    }
                    style={projectsBoardStyle}
                    laneStyle={projectsLaneStyle}
                    customCard={customCardComponent}
                    customLaneHeader={ProjectsLaneHeader as any}
                    laneFooter={EmptyComponent as any}
                    customAddCardLink={EmptyComponent as any}
                    newCardForm={EmptyComponent as any}
                    newLaneSection={ProjectsNewLaneSection as any}
                    newLaneForm={EmptyComponent as any}
                    handleOnCardDragEnd={handleOnCardDragEnd}
                    handleOnColumnDragEnd={handleOnColumnDragEnd}
                    eventBus={setEventBus}
                  />
                </PageKeyProvider>
              </div>
            ) : (
              !anyError && (
                <Box sx={{ pl: 2.5, pt: 2.5 }}>
                  <h5>Loading...</h5>
                </Box>
              )
            )}

            {modalType == ModalTypes.CreateTaskModal ? (
              <TaskDetailModal
                open={isModalOpen}
                mode="add"
                detailRouteId={routes?.createTaskRouteId ?? 0}
                initialValues={{
                  // Map to detail form data fields expected by the backend metadata
                  TtProject: modalParameters?.projectId ?? 0,
                  TtPhase: modalParameters?.taskPhaseId,
                  TtProjectReleases: modalParameters?.projectReleaseId ?? selectedReleaseId ?? 0,
                  // Ensure Priority is set (Medium) so backend won't reject the POST
                  PriorityId: modalParameters?.priorityId ?? 3,
                }}
                projectScopedAllowedIds={{
                  releaseIds: (project?.projectReleases || []).map(
                    (release: any) => release.releaseId
                  ),
                  employeeIds: (developers || [])
                    .filter((developer: any) => isActiveEmployeeRow(developer))
                    .map((developer: any) => developer.crmRelationId),
                }}
                onClose={closeModal}
                onCreated={() => {
                  if (selectedProjectId == null) return;

                  void fetchProjectPhases(
                    selectedProjectId,
                    selectedReleaseId != null && selectedReleaseId > 0
                      ? selectedReleaseId
                      : undefined
                  );
                }}
              />
            ) : (
              <AddNewEntityDialog
                isOpen={isModalOpen}
                onCreateEntity={onCreateEntity}
                onCreateEntities={onCreateEntities}
                onClose={closeModal}
                modalParameters={modalParameters}
              />
            )}
            {showConfirmTaskPhaseChangeDialog.isOpen && (
              <ConfirmTaskPhaseChangeDialog
                isOpen={showConfirmTaskPhaseChangeDialog.isOpen}
                modalParameters={showConfirmTaskPhaseChangeDialog.modalParameters}
                onConfirm={(result: number) => {
                  showConfirmTaskPhaseChangeDialog.onConfirm?.(result);
                }}
                onCancel={() => showConfirmTaskPhaseChangeDialog.onCancel?.()}
                onClose={() => showConfirmTaskPhaseChangeDialog.onClose?.()}
              />
            )}
            {openCumulativeDiagram && (
              <DiagramContainer
                project={project}
                defaultReleaseId={(selectedReleaseId ?? 0) as number}
                open={openCumulativeDiagram}
                onClose={() => setOpenCumulativeDiagram(false)}
                routes={routes}
              />
            )}
            {assignmentTask && (
              <Provider store={mainStore}>
                <AssignTaskDialog
                  open
                  taskId={assignmentTask.id}
                  taskName={assignmentTask.name}
                  anchorEl={assignmentTask.anchorEl}
                  detailRouteId={routes?.editTaskRouteId ?? 0}
                  developers={(developers || []).map((developer: any) => {
                    const assignedUser = project?.projectPhases
                      ?.flatMap((phase: any) => phase.phaseTasks ?? [])
                      .map((task: any) => task.assignedUser)
                      .find(
                        (user: any) => user && String(user.id) === String(developer.crmRelationId)
                      );

                    return {
                      ...developer,
                      initials:
                        assignedUser?.initials ??
                        developer.crmRelationInitials ??
                        developer.initials ??
                        '',
                      profilePhotoUrl:
                        assignedUser?.profilePhotoUrl ??
                        developer.crmRelationProfilePhotoUrl ??
                        developer.profilePhotoUrl,
                      hasProfilePhoto: assignedUser?.hasProfilePhoto ?? developer.hasProfilePhoto,
                    };
                  })}
                  onClose={() => setAssignmentTask(null)}
                  onAssigned={() => {
                    setAssignmentTask(null);
                    if (selectedProjectId != null) {
                      void fetchProjectPhases(
                        selectedProjectId,
                        selectedReleaseId != null && selectedReleaseId > 0
                          ? selectedReleaseId
                          : undefined
                      );
                    }
                  }}
                />
              </Provider>
            )}
            {assignedUserPreview && (
              <AssignedUserPreviewPopover
                user={assignedUserPreview.user}
                anchorEl={assignedUserPreview.anchorEl}
                onClose={() => setAssignedUserPreview(null)}
              />
            )}
          </div>
        ) : (
          <NotAssignedProjectBox
            projects={projects}
            setSelectedProjectId={setSelectedProjectId}
            selectedProjectId={selectedProjectId!}
            error={anyError ?? ''}
          />
        )}
        {hasPermissionError && (
          <NoPermissionDialog
            isOpen={!!hasPermissionError}
            onClose={() => setHasPermissionError(null)}
            message={hasPermissionError}
          />
        )}
        {isSelectionModalOpen && routes?.editProjectReleaseDetailRoute && (
          <ProjectReleaseModal
            projects={projects}
            selectedProjectId={selectedProjectId!}
            selectedReleaseId={selectedReleaseId!}
            onConfirm={handleModalSelection}
            closeModal={() => setIsSelectionModalOpen(false)}
            projectName={currentProjectName}
            routes={routes.editProjectReleaseDetailRoute}
          />
        )}
      </div>
    </KanbanViewContext.Provider>
  );
};

export default Kanban;
